import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, BACKEND_URL } from '../../firebase/config';
import { signInAnonymously } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { ArrowLeft, User, Lock, Users } from 'lucide-react';

function StaffLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const clearSessions = () => {
    sessionStorage.removeItem('adminSession');
    sessionStorage.removeItem('userSession');
    sessionStorage.removeItem('studentSession');
  };

  const openDeveloperDashboard = async (session, uid) => {
    clearSessions();
    sessionStorage.setItem('adminSession', JSON.stringify({
      ...session,
      uid,
      username: session.username || session.loginId || username.trim(),
      originalRole: session.role,
      role: 'administrator',
      isAdmin: true,
      isDeveloper: true
    }));
    navigate('/admin-dashboard');
  };

  const tryHodDeveloperLogin = async (uid) => {
    const response = await fetch(`${BACKEND_URL.replace(/\/$/, '')}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        loginType: 'hod',
        loginId: username.trim(),
        password
      })
    });
    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result.success) {
      return false;
    }

    await openDeveloperDashboard(result.session, uid);
    return true;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Sign in anonymously to Firebase (no password needed in Firebase Auth)
      const userCredential = await signInAnonymously(auth);
      const uid = userCredential.user.uid;
      console.log('✅ Signed in anonymously to Firebase');

      if (await tryHodDeveloperLogin(uid)) {
        return;
      }

      // Query Firestore users collection for matching username
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('username', '==', username));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('Invalid username or password. Please try again.');
        setLoading(false);
        return;
      }

      // Get the first matching user
      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();

      // Check password
      if (userData.password !== password) {
        setError('Invalid username or password. Please try again.');
        setLoading(false);
        return;
      }

      // Validate role exists
      if (!userData.role) {
        setError('Invalid role. Contact administrator.');
        setLoading(false);
        return;
      }

      // Store user session based on role
      if (userData.role === 'administrator' || userData.role === 'admin') {
        // Store admin session separately for admin dashboard
        sessionStorage.setItem('adminSession', JSON.stringify({
          ...userData,
          uid,
          username: userData.username || username,
          isAdmin: true,
          isDeveloper: true
        }));
        console.log('✅ Admin login successful!', {
          name: userData.name,
          role: userData.role,
          username: userData.username
        });
        navigate('/admin-dashboard');
      } else {
        // Store regular user session
        sessionStorage.setItem('userSession', JSON.stringify({
          ...userData,
          username: userData.username || username
        }));
        console.log('✅ Login successful!', {
          name: userData.name,
          role: userData.role,
          username: userData.username
        });
        navigate('/dashboard');
      }
      
    } catch (err) {
      console.error('Login error:', err);
      setError('Developer login failed. Use the current HOD Login ID and password here.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 to-green-700 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center text-white hover:text-green-200 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </button>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Developer Login</h2>
            <p className="text-gray-600 mt-2">Use the current HOD login ID and password</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Logging in...' : 'Login to Dashboard'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            <p>Forgot password?</p>
            <p className="text-green-600 hover:underline cursor-pointer">Contact HOD</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StaffLogin;
